import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Footer from "./Footer";
import Header from "./Header";

const initialFormState = {
   centerName: "",
   businessName: "",
   email: "",
   phone: "",
   country: "",
   street: "",
   city: "",
   state: "",
   borough: "",
   zip: "",
   password: "",
   confirmPassword: "",
   ein: "",
   tenant_id: ""
};

export default function AddRedemptionCenter() {
   const navigate = useNavigate();

   const [tenant, setTenant] = useState([]);
   const [form, setForm] = useState(initialFormState);
   const [submitting, setSubmitting] = useState(false);
   const [passwordError, setPasswordError] = useState("");

   /* 🔹 SERVICE AREA STATE */
   const [tenantServiceAreas, setTenantServiceAreas] = useState([]);
   const [availableStates, setAvailableStates] = useState([]);
   const [availableBoroughs, setAvailableBoroughs] = useState([]);
   const [availableZips, setAvailableZips] = useState([]);
   const [noServiceArea, setNoServiceArea] = useState(false);

   /* ================= TENANTS ================= */
   useEffect(() => {
      const fetchTenants = async () => {
         try {
            const res = await axios.get("/auth/tenants-dropdown");
            setTenant(res.data);
         } catch (err) {
            console.error("Failed to load tenants", err);
         }
      };
      fetchTenants();
   }, []);

   /* ================= SERVICE AREAS BY TENANT ================= */
   useEffect(() => {
      if (!form.tenant_id) {
         setTenantServiceAreas([]);
         setAvailableStates([]);
         setAvailableBoroughs([]);
         setAvailableZips([]);
         setNoServiceArea(false);
         return;
      }

      const fetchServiceAreas = async () => {
         try {
            const res = await axios.get(
               `/auth/tenant/service-area/${form.tenant_id}`
            );

            if (!res.data || res.data.length === 0) {
               // 🚫 No service area
               setNoServiceArea(true);
               setTenantServiceAreas([]);
               setAvailableStates([]);
               setAvailableBoroughs([]);
               setAvailableZips([]);
               return;
            }

            // ✅ Service areas exist
            setNoServiceArea(false);
            setTenantServiceAreas(res.data);

            const states = [...new Set(res.data.map(a => a.state))];
            setAvailableStates(states);
         } catch (err) {
            console.error("Failed to load service areas", err);
            setNoServiceArea(true);
         }
      };

      fetchServiceAreas();
   }, [form.tenant_id]);

   /* ================= STATE → BOROUGH ================= */
   useEffect(() => {
      if (!form.state) {
         setAvailableBoroughs([]);
         setAvailableZips([]);
         return;
      }
      const boroughs = tenantServiceAreas
         .filter(a => a.state === form.state)
         .map(a => a.borough);
      setAvailableBoroughs([...new Set(boroughs)]);
   }, [form.state, tenantServiceAreas]);

   /* ================= BOROUGH → ZIP ================= */
   useEffect(() => {
      if (!form.borough) {
         setAvailableZips([]);
         return;
      }
      const zips = tenantServiceAreas
         .filter(
            a =>
               a.state === form.state &&
               a.borough === form.borough
         )
         .map(a => a.zipcode);
      setAvailableZips([...new Set(zips)]);
   }, [form.borough, form.state, tenantServiceAreas]);

   /* ================= HANDLE CHANGE ================= */
   const handleChange = (e) => {
      const { id, value } = e.target;

      setForm(prev => ({
         ...prev,
         [id]: value,
         ...(id === "tenant_id" && { state: "", borough: "", zip: "" }),
         ...(id === "state" && { borough: "", zip: "" }),
         ...(id === "borough" && { zip: "" })
      }));

      if (
         (id === "password" || id === "confirmPassword") &&
         form.confirmPassword !== ""
      ) {
         setPasswordError(
            id === "confirmPassword" && form.password !== value
               ? "Passwords do not match."
               : ""
         );
      }
   };

   /* ================= RESET ================= */
   const handleReset = () => {
      setForm(initialFormState);
      setPasswordError("");
      setTenantServiceAreas([]);
      setAvailableStates([]);
      setAvailableBoroughs([]);
      setAvailableZips([]);
   };

   /* ================= SUBMIT ================= */
   const handleSubmit = async (e) => {
      e.preventDefault();

      if (noServiceArea) {
         toast.error("Selected tenant has no active service areas.");
         return;
      }

      if (form.password !== form.confirmPassword) {
         toast.error("Passwords do not match.");
         return;
      }

      setSubmitting(true);
      try {
         const payload = {
            centerName: form.centerName,
            businessName: form.businessName,
            email: form.email,
            phone: form.phone,
            country: form.country,
            street: form.street,
            city: form.city,
            state: form.state,
            borough: form.borough,
            zip: form.zip,
            password: form.password,
            userType: "redemption_center",
            ein: form.ein,
            tenant_id: form.tenant_id
         };

         const res = await axios.post(
            "/auth/redemption-centers/add",
            payload
         );

         toast.success(res.data.message);
         handleReset();
      } catch (err) {
         toast.error(err.response?.data?.message || "Registration failed.");
      } finally {
         setSubmitting(false);
      }
   };

   return (
      <div>
         <Header />
         <div className="min-h-screen items-center justify-center">
            <div className="max-w-2xl mx-auto my-8 p-6 sm:p-6 md:p-8 bg-white rounded-lg shadow-[0_0_10px_0_#b2b2b2]">

               <h1 className="text-center text-2xl font-semibold mb-4">
                  Add <span className="text-[#9cc026]">Redemption Center</span>
               </h1>

               <form className="space-y-6" onSubmit={handleSubmit}>

                  <div className="grid sm:grid-cols-2 gap-4">
                     <Input label="Redemption Center Name *" id="centerName" value={form.centerName} onChange={handleChange} required />
                     <Input label="Business / Legal Name *" id="businessName" value={form.businessName} onChange={handleChange} required />
                  </div>

                  <Input label="EIN / Business Registration Number *" id="ein" value={form.ein} onChange={handleChange} required />

                  <div className="grid sm:grid-cols-2 gap-4">
                     <Input label="Email *" id="email" type="email" value={form.email} onChange={handleChange} required />
                     <Input label="Phone Number *" id="phone" value={form.phone} onChange={handleChange} required />
                  </div>

                  {/* Country / Address / City */}
                  <div className="grid sm:grid-cols-3 gap-4">
                     <Select label="Country" id="country" value={form.country} onChange={handleChange} options={["India", "USA", "UK"]} />
                     <Input label="Street Address" id="street" value={form.street} onChange={handleChange} />
                     <Input label="City" id="city" value={form.city} onChange={handleChange} required />
                  </div>

                  <div>
                     <label className="block text-sm font-medium mb-1">Select Tenant</label>
                     <select
                        id="tenant_id"
                        value={form.tenant_id}
                        onChange={handleChange}
                        required
                        className="w-full p-2 border rounded"
                     >
                        <option value="">Select Tenant</option>
                        {tenant.map(t => (
                           <option key={t.id} value={t.id}>{t.tenant_name}</option>
                        ))}
                     </select>
                  </div>
                  {noServiceArea && (
                     <div className="mt-3 p-2 rounded-md bg-red-50 border border-red-300 text-red-700 text-xs font-medium">
                        🚫 This tenant has no active service areas. Please choose a different tenant or try again later.
                     </div>
                  )}


                  {/* State / Borough / ZIP */}
                  <div className="grid sm:grid-cols-3 gap-4">
                     <Select label="State" id="state" value={form.state} onChange={handleChange} options={availableStates} disabled={!availableStates.length} />
                     <Select label="Borough" id="borough" value={form.borough} onChange={handleChange} options={availableBoroughs} disabled={!availableBoroughs.length} />
                     <Select label="ZIP" id="zip" value={form.zip} onChange={handleChange} options={availableZips} disabled={!availableZips.length} />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                     <Input label="Password *" id="password" type="password" value={form.password} onChange={handleChange} required />
                     <Input label="Confirm Password *" id="confirmPassword" type="password" value={form.confirmPassword} onChange={handleChange} required />
                  </div>

                  {passwordError && <p className="text-red-500 text-sm">{passwordError}</p>}

                  <div className="flex justify-end gap-4">
                     <button type="button" onClick={handleReset}
                        className="px-6 py-3 bg-gray-300 rounded-3xl font-semibold">
                        RESET
                     </button>
                     <button type="submit" disabled={submitting}
                        className="px-6 py-3 bg-[#54BE73] hover:bg-green-700 text-white rounded-3xl font-semibold">
                        {submitting ? "Submitting..." : "COMPLETE REGISTRATION"}
                     </button>
                  </div>

               </form>

               <ToastContainer position="top-right" autoClose={3000} />
            </div>
         </div>
         <Footer />
      </div>
   );
}

/* ================= REUSABLE COMPONENTS ================= */

const Input = ({ label, id, type = "text", value, onChange, required }) => (
   <div>
      <label htmlFor={id} className="text-sm block mb-1 font-medium">{label}</label>
      <input
         id={id}
         type={type}
         value={value}
         onChange={onChange}
         required={required}
         className="w-full p-2.5 rounded-md bg-green-50 border text-sm"
      />
   </div>
);

const Select = ({ label, id, value, onChange, options, disabled }) => (
   <div>
      <label htmlFor={id} className="text-sm block mb-1 font-medium">{label}</label>
      <select
         id={id}
         value={value}
         onChange={onChange}
         disabled={disabled}
         className="w-full p-2.5 rounded-md bg-green-50 border text-sm disabled:bg-gray-200"
      >
         <option value="">Select {label}</option>
         {options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
         ))}
      </select>
   </div>
);
